import { useState } from "react";
import { useMyCompany } from "../../api/companies";
import { apiFetch } from "../../api/client";
import type { Company, SingleResponse } from "../../api/types";
import { Building2, Mail, Globe, Save } from "lucide-react";

export function SettingsGeneralPage() {
  const { data, refetch } = useMyCompany();
  const c = data?.data;

  const [companyName, setCompanyName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [timezone, setTimezone] = useState("");
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  // Init form from loaded data
  if (c && !companyName && !contactEmail) {
    setCompanyName(c.companyName);
    setContactEmail(c.contactEmail);
    setTimezone(c.timezone ?? "");
  }

  async function handleSave() {
    setSaving(true);
    setSuccess(false);
    try {
      await apiFetch<SingleResponse<Company>>("/api/v1/companies/me", {
        method: "PUT",
        body: JSON.stringify({ companyName, contactEmail, timezone: timezone || undefined }),
      });
      setSuccess(true);
      refetch();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">General Settings</h2>

      <div className="max-w-lg rounded-lg border border-gray-200 bg-white dark:border-riot-navy-700 dark:bg-riot-navy-800 p-6">
        <div className="space-y-4">
          <div>
            <label className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-riot-navy-200">
              <Building2 size={14} className="text-gray-400 dark:text-riot-navy-400" /> Company Name
            </label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500"
            />
          </div>
          <div>
            <label className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-riot-navy-200">
              <Mail size={14} className="text-gray-400 dark:text-riot-navy-400" /> Contact Email
            </label>
            <input
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500"
            />
          </div>
          <div>
            <label className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-riot-navy-200">
              <Globe size={14} className="text-gray-400 dark:text-riot-navy-400" /> Timezone
            </label>
            <input
              type="text"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              placeholder="e.g. Africa/Johannesburg"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white placeholder:text-gray-400 focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500"
            />
          </div>

          {success && (
            <p className="rounded-md bg-green-50 px-3 py-2 text-xs text-green-700 dark:bg-green-950/30 dark:text-green-300">Settings saved successfully.</p>
          )}

          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 rounded-md bg-riot-gold-400 px-4 py-2 text-sm font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:opacity-50"
          >
            <Save size={14} /> {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>

        {c && (
          <div className="mt-6 border-t border-gray-100 pt-4 dark:border-riot-navy-700">
            <dl className="space-y-2 text-xs">
              <div className="flex justify-between"><dt className="text-gray-400 dark:text-riot-navy-400">Tenant ID</dt><dd className="font-mono text-gray-600 dark:text-riot-navy-200">{c.tenantId}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-400 dark:text-riot-navy-400">Subscription Tier</dt><dd className="font-medium capitalize text-gray-700 dark:text-riot-navy-200">{c.subscriptionTier || "-"}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-400 dark:text-riot-navy-400">Status</dt><dd className="capitalize text-gray-700 dark:text-riot-navy-200">{c.status}</dd></div>
            </dl>
          </div>
        )}
      </div>
    </div>
  );
}
